import React, { useState, useEffect, useCallback } from 'react';
import { useLocalParticipant } from '@livekit/components-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { ImageIcon, Loader2 } from 'lucide-react';
import BackgroundSelector from './BackgroundSelector';
import { useBackgroundProcessor } from '../../hooks/useBackgroundProcessor';

/**
 * Compact popover button for switching virtual backgrounds during a call.
 * Placed in the meeting header/toolbar. Auto-restores the user's saved selection on mount.
 */
export default function InCallBackgroundToggle() {
  const { localParticipant } = useLocalParticipant();
  const { applyBackground, getSavedSelection, isProcessing, error } = useBackgroundProcessor();
  const [selectedBg, setSelectedBg] = useState(() => getSavedSelection());
  const [open, setOpen] = useState(false);
  const [restored, setRestored] = useState(false);

  /** Get the local camera track (if published). */
  const getCameraTrack = useCallback(() => {
    if (!localParticipant) return null;
    const pub = localParticipant.getTrackPublication('camera');
    return pub?.track ?? null;
  }, [localParticipant]);

  // Auto-restore saved background when camera track becomes available
  useEffect(() => {
    if (restored) return;

    const saved = getSavedSelection();
    if (!saved || saved.type === 'none') {
      setRestored(true);
      return;
    }

    const track = getCameraTrack();
    if (!track) return; // will re-run when localParticipant updates

    setRestored(true);
    setSelectedBg(saved);
    applyBackground(track, saved).catch((err) =>
      console.error('[InCallBackgroundToggle] Failed to restore background:', err),
    );
  }, [localParticipant, restored, getSavedSelection, getCameraTrack, applyBackground]);

  const handleSelect = async (bg) => {
    setSelectedBg(bg);
    const track = getCameraTrack();
    if (track) {
      await applyBackground(track, bg);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5"
          title="Virtual background"
        >
          {isProcessing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <ImageIcon className="h-4 w-4" />
          )}
          <span className="sr-only">Virtual background</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end" side="top">
        <BackgroundSelector
          selectedId={selectedBg?.id ?? 'none'}
          onSelect={handleSelect}
          disabled={isProcessing}
        />
        {error && (
          <p className="text-xs text-destructive mt-2">
            Failed to apply background. Try again or select a different one.
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
}
